import React, { useEffect, useState } from 'react'
import { useSearchParams, Link, useLocation } from 'react-router-dom'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import Layout from '../components/layout/Layout'
import { useLanguage } from '../hooks/useLanguage'

interface PaymentStatus {
  status: 'loading' | 'success' | 'error'
  message: string
  sessionId?: string
}

export default function PaymentReturnPage() {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const { t } = useLanguage()
  const sessionId = searchParams.get('session_id')
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>({
    status: 'loading',
    message: t('common.loading')
  })

  useEffect(() => {
    // Debug logging
    console.log('PaymentReturnPage mounted')
    console.log('Current location:', location.pathname)
    console.log('Search params:', Object.fromEntries(searchParams.entries()))
    console.log('Session ID:', sessionId)
    console.log('Full URL:', window.location.href)

    // Add a small delay to ensure the component is fully mounted
    const timer = setTimeout(() => {
      if (!sessionId) {
        setPaymentStatus({
          status: 'error',
          message: t('payment.missingSessionId')
        })
        return
      }

      // Simulate payment verification
      // In a real implementation, you would verify the session with your backend
      const verifyPayment = async () => {
        try {
          // For now, we'll assume success if session_id is present
          // In production, you should verify with your backend
          setPaymentStatus({
            status: 'success',
            message: t('payment.paymentSuccess'),
            sessionId
          })
        } catch (error) {
          console.error('Payment verification error:', error)
          setPaymentStatus({
            status: 'error',
            message: t('payment.verificationError'),
            sessionId
          })
        }
      }

      verifyPayment()
    }, 100)

    return () => clearTimeout(timer)
  }, [sessionId, location, searchParams, t])

  // Add error boundary
  if (typeof window === 'undefined') {
    return (
      <Layout>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (paymentStatus.status === 'loading') {
    return (
      <Layout>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{paymentStatus.message}</p>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="flex items-center justify-center py-8 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          {paymentStatus.status === 'success' ? (
            <>
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                {t('payment.paymentCompleted')}
              </h1>
              <p className="text-gray-600 mb-6">
                {t('payment.paymentSuccessMessage')}
              </p>
              <div className="space-y-3">
                <Link
                  to="/"
                  className="block w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('payment.backToHome')}
                </Link>
                <Link
                  to="/account"
                  className="block w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('payment.goToAccount')}
                </Link>
              </div>
            </>
          ) : (
            <>
              <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                {t('payment.paymentError')}
              </h1>
              <p className="text-gray-600 mb-6">
                {paymentStatus.message}
              </p>
              <div className="space-y-3">
                <Link
                  to="/"
                  className="block w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('payment.backToHome')}
                </Link>
                <Link
                  to="/contact"
                  className="block w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t('payment.contactSupport')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  )
} 